import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { PasswordField, PasswordChecklist } from "@/components/PasswordUI";
import { passwordValid } from "@/lib/validators";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!passwordValid(pwd)) return toast.error("Your password does not meet the requirements.");
    if (pwd !== confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pwd });
      toast.success(data.message);
      nav("/login");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!token) return (
    <div className="min-h-screen grid place-items-center p-6 text-center">
      <div>
        <p className="font-display font-bold text-xl">Invalid reset link</p>
        <p className="text-muted text-sm mt-1">This password reset link is missing or malformed.</p>
        <Link to="/forgot-password" className="text-azure text-sm mt-4 inline-block hover:underline">Request a new link →</Link>
      </div>
    </div>
  );

  const mismatch = confirm.length > 0 && pwd !== confirm;

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="reset-form">
        <div className="mb-8"><Logo /></div>
        <h1 className="font-display font-bold text-2xl mb-1">Choose a new password</h1>
        <p className="text-sm text-muted mb-6">Make it strong — this protects your Team Assembly account.</p>

        <label className="label-mono text-faint">New password</label>
        <div className="mt-1.5"><PasswordField value={pwd} onChange={(e) => setPwd(e.target.value)} testId="reset-password" autoComplete="new-password" /></div>
        <PasswordChecklist value={pwd} />

        <label className="label-mono text-faint mt-4 block">Confirm password</label>
        <div className="mt-1.5"><PasswordField value={confirm} onChange={(e) => setConfirm(e.target.value)} testId="reset-confirm" autoComplete="new-password" /></div>
        {mismatch && <p className="mt-1.5 text-xs text-signal" data-testid="reset-mismatch">Passwords do not match.</p>}

        <button disabled={busy} data-testid="reset-submit"
          className="mt-6 w-full rounded-full bg-azure py-3 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
          {busy ? "Resetting…" : "Reset password"}
        </button>
        <p className="mt-6 text-sm text-muted text-center">
          <Link to="/login" className="text-azure hover:underline">Back to sign in</Link>
        </p>
      </form>
    </div>
  );
}
